import { supabase } from '../../../shared/lib/supabase';
import { explainRuleMatchFailure, selectMatchingBillingRule } from '../../../shared/utils/billingRuleEvaluator';
import { minutesUsageService } from '../../capture/services/minutesUsageService';
import { storageUsageService } from '../../capture/services/storageUsageService';
import { billingOrgSettingsService } from './billingOrgSettingsService';
import { billingPlansService } from './billingPlansService';
import { billingPricingRulesService } from './billingPricingRulesService';
import type {
  BillingInvoice,
  BillingInvoiceCreateInput,
  BillingInvoiceLineItemCreateInput,
  BillingPlan,
  BillingPlanEntitlement,
  BillingPricingRule,
  BillingPricingRuleCondition,
} from './billingTypes';

interface BillingComputationInput {
  organisationId: string;
  periodStartIso: string;
  periodEndIso: string;
  invoiceType: BillingInvoice['invoice_type'];
  billingPlanId?: string | null;
  currencyOverride?: string;
  taxRatePercentOverride?: number;
  pricesTaxInclusiveOverride?: boolean;
  taxLabelOverride?: string;
}

interface MatchLineupContext {
  averageStartingPlayers: number;
  maxStartingPlayers: number;
  accessibleMatchesCount: number;
}

/** Per-rule video calculation for preview validation. */
export interface VideoRuleCalculationBreakdown {
  ruleName: string;
  rawMinutes: number;
  billableMinutes: number;
  ratePerMinute: number;
  amount: number;
}

/** Storage calculation for preview validation. */
export interface StorageCalculationBreakdown {
  rawGb: number;
  billableGb: number;
  includedGb: number;
  ratePerGb: number;
  amount: number;
}

export interface BillingComputationResult {
  draftInput: BillingInvoiceCreateInput;
  debug: {
    videoMinutesUsage: number;
    videoMinutesBillable: number;
    videoMinutesIncluded: number;
    storageGbUsage: number;
    storageGbBillable: number;
    storageGbIncluded: number;
    enabledServices: string[];
    lineup: MatchLineupContext;
    /** Per-rule video breakdown for preview validation (minutes × rate = amount). */
    videoBreakdown: VideoRuleCalculationBreakdown[];
    /** Storage breakdown for preview validation. */
    storageBreakdown: StorageCalculationBreakdown | null;
    /** Why no rule matched for video_minutes (when amounts are zero). */
    matchFailureVideo?: string;
    /** Why no rule matched for storage (when amounts are zero). */
    matchFailureStorage?: string;
  };
}

const roundMoney = (value: number): number => Math.round(value * 100) / 100;

/** Extract max_players threshold from rule conditions (<= N) for sorting 8v8 before 11v11. */
function getMaxPlayersThreshold(
  conditions: BillingPricingRuleCondition[]
): number {
  const lineupKey = 'max_players_in_starting_lineup';
  const legacyKey = 'players_in_starting_lineup';
  for (const c of conditions) {
    if ((c.field_key === lineupKey || c.field_key === legacyKey) && c.operator === '<=') {
      const n = Number(c.field_value);
      if (!Number.isNaN(n)) return n;
    }
  }
  return Number.POSITIVE_INFINITY;
}

const toRateAmount = (rule: BillingPricingRule | null, quantity: number): number => {
  if (!rule) return 0;
  const rateValue = Number(rule.rate_value || 0);
  switch (rule.rate_type) {
    case 'per_minute':
    case 'per_gb':
      return roundMoney(quantity * rateValue);
    case 'flat':
      return roundMoney(rateValue);
    case 'slab_flat':
      return quantity > 0 ? roundMoney(rateValue) : 0;
    case 'slab_progressive':
      return roundMoney(quantity * rateValue);
    default:
      return 0;
  }
};

const applyTax = (
  subtotalInput: number,
  taxRatePercent: number,
  pricesTaxInclusive: boolean
): { subtotal: number; tax: number; total: number } => {
  if (pricesTaxInclusive) {
    const total = roundMoney(subtotalInput);
    if (taxRatePercent <= 0) {
      return { subtotal: total, tax: 0, total };
    }
    const preTax = total / (1 + taxRatePercent / 100);
    const subtotal = roundMoney(preTax);
    const tax = roundMoney(total - preTax);
    return { subtotal, tax, total };
  }

  const subtotal = roundMoney(subtotalInput);
  const tax = roundMoney((subtotal * taxRatePercent) / 100);
  const total = roundMoney(subtotal + tax);
  return { subtotal, tax, total };
};

export class BillingCalculationService {
  private async resolvePlan(input: BillingComputationInput): Promise<BillingPlan | null> {
    if (input.billingPlanId) {
      return billingPlansService.getById(input.billingPlanId);
    }

    const allPlans = await billingPlansService.getAll();
    const periodAt = new Date(input.periodStartIso).getTime();
    const candidates = allPlans
      .filter((plan) => {
        if (!plan.is_active) return false;
        if (plan.organisation_id && plan.organisation_id !== input.organisationId) return false;
        const from = new Date(plan.valid_from).getTime();
        const to = plan.valid_to ? new Date(plan.valid_to).getTime() : Number.POSITIVE_INFINITY;
        return periodAt >= from && periodAt <= to;
      })
      .sort((a, b) => {
        if (a.organisation_id === input.organisationId && b.organisation_id !== input.organisationId) return -1;
        if (a.organisation_id !== input.organisationId && b.organisation_id === input.organisationId) return 1;
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      });

    return candidates[0] || null;
  }

  private async getEnabledServices(organisationId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('organisation_api_config')
      .select('match_details, match_stats, player_stats, tactical_charts, highlights')
      .eq('organisation_id', organisationId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return [];

    const enabled: string[] = [];
    if (data.match_details) enabled.push('match_details');
    if (data.match_stats) enabled.push('match_stats');
    if (data.player_stats) enabled.push('player_stats');
    if (data.tactical_charts) enabled.push('tactical_charts');
    if (data.highlights) enabled.push('highlights');
    return enabled;
  }

  private async getLineupContext(organisationId: string): Promise<MatchLineupContext> {
    const { data: tournamentAccess, error: taErr } = await supabase
      .from('tournament_access')
      .select('tournament_id')
      .eq('organisation_id', organisationId)
      .eq('is_active', true);
    if (taErr) throw taErr;

    const { data: teamAccess, error: teamErr } = await supabase
      .from('team_access')
      .select('team_id')
      .eq('organisation_id', organisationId)
      .eq('active', true);
    if (teamErr) throw teamErr;

    const tournamentIds = (tournamentAccess || []).map((x) => x.tournament_id);
    const teamIds = (teamAccess || []).map((x) => x.team_id);
    if (tournamentIds.length === 0 || teamIds.length === 0) {
      return { averageStartingPlayers: 0, maxStartingPlayers: 0, accessibleMatchesCount: 0 };
    }

    const { data: matches, error: matchesErr } = await supabase
      .from('matches')
      .select('id, home_team_id, away_team_id')
      .in('tournament_id', tournamentIds)
      .eq('active', true);
    if (matchesErr) throw matchesErr;

    const accessibleMatchIds = (matches || [])
      .filter((m) => teamIds.includes(m.home_team_id) || teamIds.includes(m.away_team_id))
      .map((m) => m.id);

    if (accessibleMatchIds.length === 0) {
      return { averageStartingPlayers: 0, maxStartingPlayers: 0, accessibleMatchesCount: 0 };
    }

    const { data: lineupRows, error: lineupErr } = await supabase
      .from('match_lineup')
      .select('match_id, team_id')
      .in('match_id', accessibleMatchIds)
      .eq('is_starting', true)
      .eq('is_bench', false);
    if (lineupErr) throw lineupErr;

    const countByMatchTeam = new Map<string, number>();
    (lineupRows || []).forEach((row) => {
      const key = `${row.match_id}:${row.team_id}`;
      countByMatchTeam.set(key, (countByMatchTeam.get(key) || 0) + 1);
    });

    const counts = Array.from(countByMatchTeam.values());
    if (counts.length === 0) {
      return {
        averageStartingPlayers: 0,
        maxStartingPlayers: 0,
        accessibleMatchesCount: accessibleMatchIds.length,
      };
    }

    const total = counts.reduce((sum, value) => sum + value, 0);
    return {
      averageStartingPlayers: total / counts.length,
      maxStartingPlayers: Math.max(...counts),
      accessibleMatchesCount: accessibleMatchIds.length,
    };
  }

  /** Per-match max players in starting lineup (for 8v8 vs 11v11 billing split). */
  private async getLineupPerMatch(organisationId: string): Promise<Map<string, number>> {
    const { data: tournamentAccess, error: taErr } = await supabase
      .from('tournament_access')
      .select('tournament_id')
      .eq('organisation_id', organisationId)
      .eq('is_active', true);
    if (taErr) throw taErr;
    const { data: teamAccess, error: teamErr } = await supabase
      .from('team_access')
      .select('team_id')
      .eq('organisation_id', organisationId)
      .eq('active', true);
    if (teamErr) throw teamErr;
    const tournamentIds = (tournamentAccess || []).map((x) => x.tournament_id);
    const teamIds = (teamAccess || []).map((x) => x.team_id);
    if (tournamentIds.length === 0 || teamIds.length === 0) return new Map();

    const { data: matches, error: matchesErr } = await supabase
      .from('matches')
      .select('id, home_team_id, away_team_id')
      .in('tournament_id', tournamentIds)
      .eq('active', true);
    if (matchesErr) throw matchesErr;
    const accessibleMatchIds = (matches || [])
      .filter((m) => teamIds.includes(m.home_team_id) || teamIds.includes(m.away_team_id))
      .map((m) => m.id);
    if (accessibleMatchIds.length === 0) return new Map();

    const { data: lineupRows, error: lineupErr } = await supabase
      .from('match_lineup')
      .select('match_id, team_id')
      .in('match_id', accessibleMatchIds)
      .eq('is_starting', true)
      .eq('is_bench', false);
    if (lineupErr) throw lineupErr;

    const countByMatchTeam = new Map<string, number>();
    (lineupRows || []).forEach((row) => {
      const key = `${row.match_id}:${row.team_id}`;
      countByMatchTeam.set(key, (countByMatchTeam.get(key) || 0) + 1);
    });
    const maxByMatch = new Map<string, number>();
    accessibleMatchIds.forEach((matchId) => {
      const homeId = (matches || []).find((m) => m.id === matchId)?.home_team_id;
      const awayId = (matches || []).find((m) => m.id === matchId)?.away_team_id;
      const homeCount = homeId ? countByMatchTeam.get(`${matchId}:${homeId}`) ?? 0 : 0;
      const awayCount = awayId ? countByMatchTeam.get(`${matchId}:${awayId}`) ?? 0 : 0;
      const max = Math.max(homeCount, awayCount);
      if (max > 0) maxByMatch.set(matchId, max);
    });
    return maxByMatch;
  }

  async computeInvoiceDraft(input: BillingComputationInput): Promise<BillingComputationResult> {
    const [plan, rules, minutesWithBreakdown, storageUsage, orgSettings, enabledServices, lineup, lineupPerMatch] =
      await Promise.all([
        this.resolvePlan(input),
        billingPricingRulesService.getAll(),
        minutesUsageService.getMinutesUsageByOrgWithMatchBreakdown(
          input.organisationId,
          input.periodStartIso,
          input.periodEndIso
        ),
        storageUsageService.getStorageUsageByOrg(input.organisationId),
        billingOrgSettingsService.getByOrganisationId(input.organisationId),
        this.getEnabledServices(input.organisationId),
        this.getLineupContext(input.organisationId),
        this.getLineupPerMatch(input.organisationId),
      ]);

    const minutesUsage = minutesWithBreakdown;

    const scopedRules = rules.filter((rule) => !rule.organisation_id || rule.organisation_id === input.organisationId);
    const conditionsByRuleId: Record<string, BillingPricingRuleCondition[]> = {};
    await Promise.all(
      scopedRules.map(async (rule) => {
        conditionsByRuleId[rule.id] = await billingPricingRulesService.getConditions(rule.id);
      })
    );

    const entitlements: BillingPlanEntitlement[] = plan
      ? await billingPlansService.getEntitlementsByPlanId(plan.id)
      : [];
    const includedVideoMinutes = Number(entitlements.find((e) => e.metric === 'video_minutes')?.included_units || 0);
    const includedStorageGb = Number(entitlements.find((e) => e.metric === 'storage')?.included_units || 0);

    const videoMinutesUsage = Number(minutesUsage.totalMinutes || 0);
    const storageGbUsage = Number(storageUsage.totalGB || 0);
    const videoMinutesBillable = Math.max(videoMinutesUsage - includedVideoMinutes, 0);
    const storageGbBillable = Math.max(storageGbUsage - includedStorageGb, 0);

    const baseValues = {
      players_in_starting_lineup: Number(lineup.averageStartingPlayers.toFixed(2)),
      max_players_in_starting_lineup: lineup.maxStartingPlayers,
      accessible_matches_count: lineup.accessibleMatchesCount,
      video_minutes: videoMinutesUsage,
      storage_gb: storageGbUsage,
    };

    const periodEndMs = new Date(input.periodEndIso).getTime();
    const nowMs = Date.now();
    const applyToPastPeriods = periodEndMs < nowMs;

    const ruleContextBase = {
      organisation_id: input.organisationId,
      values: baseValues,
      timestamp: input.periodEndIso,
      applyToPastPeriods,
    };

    const { matchedRule: videoRule } = selectMatchingBillingRule({
      rules: scopedRules,
      conditionsByRuleId,
      context: { ...ruleContextBase, metric: 'video_minutes' },
    });

    const { matchedRule: storageRule } = selectMatchingBillingRule({
      rules: scopedRules,
      conditionsByRuleId,
      context: { ...ruleContextBase, metric: 'storage' },
    });

    const { matchedRule: serviceComboRule } = selectMatchingBillingRule({
      rules: scopedRules,
      conditionsByRuleId,
      context: {
        ...ruleContextBase,
        metric: 'service_combo',
        enabledServices,
      },
    });

    const videoContext = {
      ...ruleContextBase,
      metric: 'video_minutes' as const,
    };
    const storageContext = {
      ...ruleContextBase,
      metric: 'storage' as const,
    };
    const videoRules = scopedRules
      .filter((r) => r.metric === 'video_minutes' && r.is_active)
      .sort((a, b) => {
        const ta = getMaxPlayersThreshold(conditionsByRuleId[a.id] || []);
        const tb = getMaxPlayersThreshold(conditionsByRuleId[b.id] || []);
        if (ta !== tb) return ta - tb;
        return (a.priority || 0) - (b.priority || 0);
      });
    const fallbackVideoRule = videoRules.length > 0 ? videoRules[videoRules.length - 1] : null;

    const ruleMinutesBucket = new Map<string, number>();
    let unallocatedMinutes = 0;
    if (
      (input.invoiceType === 'usage_settlement_end' || input.invoiceType === 'adjustment_only') &&
      videoMinutesUsage > 0 &&
      minutesWithBreakdown.matchBreakdown?.length
    ) {
      for (const item of minutesWithBreakdown.matchBreakdown) {
        if (item.matchId == null) {
          unallocatedMinutes += item.minutes;
          continue;
        }
        // Use 11 when match has no lineup data so it matches 11v11 (not 8v8)
        const maxPlayers = lineupPerMatch.get(item.matchId) ?? 11;
        const contextValues = { ...baseValues, max_players_in_starting_lineup: maxPlayers };
        const { matchedRule } = selectMatchingBillingRule({
          rules: videoRules,
          conditionsByRuleId,
          context: {
            ...ruleContextBase,
            metric: 'video_minutes',
            values: contextValues,
          },
        });
        const rule = matchedRule ?? fallbackVideoRule;
        if (rule) {
          ruleMinutesBucket.set(rule.id, (ruleMinutesBucket.get(rule.id) ?? 0) + item.minutes);
        }
      }
      // Split unallocated minutes (e.g. Video Library not linked to match) by match count per rule
      if (unallocatedMinutes > 0 && videoRules.length > 0 && lineupPerMatch.size > 0) {
        const thresholdFirst = getMaxPlayersThreshold(conditionsByRuleId[videoRules[0].id] || []);
        const rule8v8 = thresholdFirst <= 10 ? videoRules[0] : null;
        const rule11v11 = videoRules.length > 1 ? videoRules[1] : videoRules[0];
        let count8v8 = 0;
        let count11v11 = 0;
        lineupPerMatch.forEach((maxP) => {
          if (rule8v8 && maxP <= thresholdFirst) count8v8 += 1;
          else count11v11 += 1;
        });
        const totalCount = count8v8 + count11v11;
        if (totalCount > 0) {
          if (rule8v8 && count8v8 > 0) {
            const add = (unallocatedMinutes * count8v8) / totalCount;
            ruleMinutesBucket.set(rule8v8.id, (ruleMinutesBucket.get(rule8v8.id) ?? 0) + add);
          }
          if (count11v11 > 0) {
            const add = (unallocatedMinutes * count11v11) / totalCount;
            ruleMinutesBucket.set(rule11v11.id, (ruleMinutesBucket.get(rule11v11.id) ?? 0) + add);
          }
        } else if (fallbackVideoRule) {
          ruleMinutesBucket.set(fallbackVideoRule.id, (ruleMinutesBucket.get(fallbackVideoRule.id) ?? 0) + unallocatedMinutes);
        }
      } else if (unallocatedMinutes > 0 && fallbackVideoRule) {
        ruleMinutesBucket.set(fallbackVideoRule.id, (ruleMinutesBucket.get(fallbackVideoRule.id) ?? 0) + unallocatedMinutes);
      }
    }

    const matchFailureVideo =
      !videoRule && scopedRules.some((r) => r.metric === 'video_minutes')
        ? explainRuleMatchFailure({ rules: scopedRules, conditionsByRuleId, context: videoContext })
        : undefined;
    const matchFailureStorage =
      !storageRule && scopedRules.some((r) => r.metric === 'storage')
        ? explainRuleMatchFailure({ rules: scopedRules, conditionsByRuleId, context: storageContext })
        : undefined;

    const lineItems: BillingInvoiceLineItemCreateInput[] = [];
    if (input.invoiceType === 'subscription_start' && plan && Number(plan.base_subscription_fee || 0) > 0) {
      lineItems.push({
        line_type: 'subscription',
        metric: null,
        description: `Subscription fee - ${plan.name}`,
        quantity: 1,
        unit_price: Number(plan.base_subscription_fee || 0),
        amount: roundMoney(Number(plan.base_subscription_fee || 0)),
        metadata: { billing_plan_id: plan.id },
      });
    }

    const videoBreakdown: VideoRuleCalculationBreakdown[] = [];
    let storageBreakdown: StorageCalculationBreakdown | null = null;

    if (input.invoiceType === 'usage_settlement_end' || input.invoiceType === 'adjustment_only') {
      if (videoMinutesBillable > 0) {
        if (ruleMinutesBucket.size > 0) {
          const totalVideoMinutes = videoMinutesUsage;
          for (const [ruleId, rawMinutes] of ruleMinutesBucket) {
            const rule = scopedRules.find((r) => r.id === ruleId);
            if (!rule || rawMinutes <= 0) continue;
            const billableForRule =
              totalVideoMinutes > 0
                ? roundMoney((rawMinutes / totalVideoMinutes) * videoMinutesBillable * 10000) / 10000
                : 0;
            if (billableForRule <= 0) continue;
            const ratePerMin = Number(rule.rate_value || 0);
            const amount = toRateAmount(rule, billableForRule);
            videoBreakdown.push({
              ruleName: rule.name,
              rawMinutes,
              billableMinutes: billableForRule,
              ratePerMinute: ratePerMin,
              amount,
            });
            lineItems.push({
              line_type: 'usage',
              metric: 'video_minutes',
              description: `Video minutes usage (${rule.name})`,
              quantity: Number(billableForRule.toFixed(4)),
              unit_price: ratePerMin,
              amount,
              metadata: {
                rule_id: rule.id,
                raw_usage_minutes: Number(rawMinutes.toFixed(4)),
                included_units: 0,
                billable_units: Number(billableForRule.toFixed(4)),
                players_in_starting_lineup_avg: Number(lineup.averageStartingPlayers.toFixed(2)),
              },
            });
          }
        } else {
          const ratePerMin = videoRule ? Number(videoRule.rate_value || 0) : 0;
          const amount = toRateAmount(videoRule, videoMinutesBillable);
          videoBreakdown.push({
            ruleName: videoRule?.name ?? 'Video minutes',
            rawMinutes: videoMinutesUsage,
            billableMinutes: videoMinutesBillable,
            ratePerMinute: ratePerMin,
            amount,
          });
          lineItems.push({
            line_type: 'usage',
            metric: 'video_minutes',
            description: videoRule ? `Video minutes usage (${videoRule.name})` : 'Video minutes usage',
            quantity: Number(videoMinutesBillable.toFixed(4)),
            unit_price: videoRule ? ratePerMin : null,
            amount,
            metadata: {
              rule_id: videoRule?.id || null,
              raw_usage_minutes: Number(videoMinutesUsage.toFixed(4)),
              included_units: includedVideoMinutes,
              billable_units: Number(videoMinutesBillable.toFixed(4)),
              players_in_starting_lineup_avg: Number(lineup.averageStartingPlayers.toFixed(2)),
            },
          });
        }
      }

      if (storageGbBillable > 0) {
        const storageRate = storageRule ? Number(storageRule.rate_value || 0) : 0;
        const storageAmount = toRateAmount(storageRule, storageGbBillable);
        storageBreakdown = {
          rawGb: storageGbUsage,
          billableGb: storageGbBillable,
          includedGb: includedStorageGb,
          ratePerGb: storageRate,
          amount: storageAmount,
        };
        lineItems.push({
          line_type: 'usage',
          metric: 'storage',
          description: storageRule ? `Storage usage (${storageRule.name})` : 'Storage usage',
          quantity: Number(storageGbBillable.toFixed(4)),
          unit_price: storageRate,
          amount: storageAmount,
          metadata: {
            rule_id: storageRule?.id || null,
            raw_usage_gb: Number(storageGbUsage.toFixed(4)),
            included_units: includedStorageGb,
            billable_units: Number(storageGbBillable.toFixed(4)),
          },
        });
      }

      if (serviceComboRule) {
        lineItems.push({
          line_type: 'usage',
          metric: 'service_combo',
          description: `Service combo (${serviceComboRule.name})`,
          quantity: 1,
          unit_price: Number(serviceComboRule.rate_value || 0),
          amount: toRateAmount(serviceComboRule, 1),
          metadata: {
            rule_id: serviceComboRule.id,
            enabled_services: enabledServices,
            match_mode: serviceComboRule.service_combo_match_mode,
            rate_mode: serviceComboRule.service_combo_rate_mode,
          },
        });
      }
    }

    if (lineItems.length === 0) {
      lineItems.push({
        line_type: 'usage',
        metric: 'no_billable_usage',
        description: 'No billable usage for selected period',
        quantity: 0,
        unit_price: 0,
        amount: 0,
      });
    }

    const preTaxAmount = roundMoney(lineItems.reduce((sum, item) => sum + Number(item.amount || 0), 0));
    const taxRate =
      input.taxRatePercentOverride !== undefined
        ? Number(input.taxRatePercentOverride || 0)
        : Number(orgSettings?.default_tax_rate_percent || 0);
    const pricesTaxInclusive =
      input.pricesTaxInclusiveOverride !== undefined
        ? Boolean(input.pricesTaxInclusiveOverride)
        : Boolean(orgSettings?.prices_tax_inclusive);
    const taxLabel = input.taxLabelOverride || orgSettings?.tax_label || 'Tax';
    const totals = applyTax(preTaxAmount, taxRate, pricesTaxInclusive);
    const currency = input.currencyOverride || plan?.currency || orgSettings?.currency || 'INR';

    return {
      draftInput: {
        organisation_id: input.organisationId,
        billing_plan_id: plan?.id || null,
        invoice_type: input.invoiceType,
        period_start: input.periodStartIso,
        period_end: input.periodEndIso,
        currency,
        subtotal_amount: totals.subtotal,
        tax_amount: totals.tax,
        total_amount: totals.total,
        tax_rate_percent: taxRate,
        prices_tax_inclusive: pricesTaxInclusive,
        tax_label: taxLabel,
        line_items: lineItems,
      },
      debug: {
        videoMinutesUsage,
        videoMinutesBillable,
        videoMinutesIncluded: includedVideoMinutes,
        storageGbUsage,
        storageGbBillable,
        storageGbIncluded: includedStorageGb,
        enabledServices,
        lineup,
        videoBreakdown,
        storageBreakdown,
        matchFailureVideo,
        matchFailureStorage,
      },
    };
  }
}

export const billingCalculationService = new BillingCalculationService();
